import React, { useEffect, useRef } from 'react';
import { Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { DivIcon, LatLngBounds } from 'leaflet';
import { useRoute } from '../../../contexts/RouteContext';
import { formatDistance, formatDuration, getTransportModeIcon, RoutePoint } from '../../../services/routing';
import { createFriendIcon, userRouteOriginIcon, getPhotoUrl } from '../utils';

interface RoutePolylineProps {
  language?: 'fr' | 'en';
}

// Colors using the app's color palette (turquoise theme)
const ROUTE_COLORS = {
  selected: '#00aa9f', // --accent-primary
  alternative: '#00aa9f', // Same color but transparent
  hover: '#00745e', // --accent-hover
};

// Create custom marker icons
const createMarkerIcon = (label: string, color: string) => {
  return new DivIcon({
    className: 'route-marker-icon',
    html: `
      <div style="
        background: ${color};
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">${label}</div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Brand colors from color chart - All route markers are orange for consistency
const MARKER_COLORS = {
  origin: '#e1723c',      // Orange
  destination: '#e1723c', // Orange
  waypoint: '#e1723c',    // Orange (for numbered markers)
};

const originIcon = createMarkerIcon('A', MARKER_COLORS.origin);
const destinationIcon = createMarkerIcon('B', MARKER_COLORS.destination);

export const RoutePolyline: React.FC<RoutePolylineProps> = ({ language = 'fr' }) => {
  const map = useMap();
  const {
    isRoutingMode,
    origin,
    destination,
    waypoints,
    routingResult,
    selectedRouteIndex,
    setSelectedRouteIndex,
    selectedRoute,
    transportMode,
  } = useRoute();

  const locale = language === 'fr' ? 'fr' : 'en';

  // Track last fitted result to avoid refitting on every render
  const lastFittedResultRef = useRef<string | null>(null);

  // Close all popups when routing mode is activated
  useEffect(() => {
    if (isRoutingMode) {
      map.closePopup();
    }
  }, [isRoutingMode, map]);

  // Fit bounds when routing result changes
  useEffect(() => {
    if (!routingResult || !routingResult.routes.length) {
      lastFittedResultRef.current = null;
      return;
    }

    // Create a unique key for this result to avoid refitting
    const resultKey = `${routingResult.routes[0].id}-${routingResult.routes.length}`;
    if (lastFittedResultRef.current === resultKey) {
      return; // Already fitted this result
    }
    lastFittedResultRef.current = resultKey;

    // Calculate bounds from all routes
    const bounds = new LatLngBounds([]);

    routingResult.routes.forEach(route => {
      route.geometry.forEach(point => {
        bounds.extend(point);
      });
    });

    // Also include origin, destination and waypoints
    if (origin) {
      bounds.extend([Number(origin.lat), Number(origin.lng)]);
    }
    if (destination) {
      bounds.extend([Number(destination.lat), Number(destination.lng)]);
    }
    waypoints.forEach(wp => {
      bounds.extend([Number(wp.lat), Number(wp.lng)]);
    });

    // Fit the map to show all routes with padding
    if (bounds.isValid()) {
      // Add padding to account for the route panel
      const isMobile = window.innerWidth <= 768;
      const screenHeight = window.innerHeight;

      // Offset to account for the route panel
      // Desktop: panel is on left (450px wide)
      // Mobile: panel is at bottom (50vh = 50% of screen height)
      const paddingOptions = isMobile
        ? {
            paddingTopLeft: [30, 30] as [number, number],
            paddingBottomRight: [30, screenHeight * 0.52] as [number, number], // 50vh panel + margin
          }
        : {
            paddingTopLeft: [470, 50] as [number, number],  // 450px panel + 20px margin
            paddingBottomRight: [50, 50] as [number, number],
          };

      map.fitBounds(bounds, {
        ...paddingOptions,
        maxZoom: 16, // Don't zoom in too much
        animate: true,
        duration: 0.5,
      });
    }
  }, [routingResult, origin, destination, waypoints, map]);

  if (!isRoutingMode || !routingResult) return null;

  return (
    <>
      {/* Alternative routes (render first so they're behind) */}
      {routingResult.routes.map((route, index) => {
        if (index === selectedRouteIndex) return null;

        return (
          <Polyline
            key={route.id}
            positions={route.geometry}
            pathOptions={{
              color: ROUTE_COLORS.alternative,
              weight: 5,
              opacity: 0.35, // More transparent for alternatives
              // No dashArray - solid line
            }}
            eventHandlers={{
              click: () => setSelectedRouteIndex(index),
              mouseover: (e) => {
                e.target.setStyle({ opacity: 0.7, weight: 7 });
              },
              mouseout: (e) => {
                e.target.setStyle({ opacity: 0.35, weight: 5 });
              },
            }}
          >
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <strong>Alternative {index + 1}</strong>
                <br />
                {getTransportModeIcon(transportMode)} {formatDuration(route.duration, locale)}
                <br />
                📏 {formatDistance(route.distance, locale)}
                <br />
                <button
                  onClick={() => setSelectedRouteIndex(index)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    background: ROUTE_COLORS.selected,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Sélectionner
                </button>
              </div>
            </Popup>
          </Polyline>
        );
      })}

      {/* Selected route (render on top) */}
      {selectedRoute && (
        <Polyline
          positions={selectedRoute.geometry}
          pathOptions={{
            color: ROUTE_COLORS.selected,
            weight: 6,
            opacity: 0.9,
          }}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <strong>{getTransportModeIcon(transportMode)} Itinéraire sélectionné</strong>
              <br />
              ⏱️ {formatDuration(selectedRoute.duration, locale)}
              <br />
              📏 {formatDistance(selectedRoute.distance, locale)}
              <br />
              <span style={{ fontSize: '11px', color: '#666' }}>
                via {routingResult.provider.toUpperCase()}
              </span>
            </div>
          </Popup>
        </Polyline>
      )}

      {/* Origin marker - renders based on markerType */}
      {origin && (() => {
        // GPS origin: orange pulsing marker
        if (origin.markerType === 'gps') {
          return (
            <Marker
              position={[Number(origin.lat), Number(origin.lng)]}
              icon={userRouteOriginIcon}
              zIndexOffset={5000}
            >
              <Popup>
                <div>
                  <strong>🚀 Départ</strong>
                  <br />
                  {origin.name || 'Ma position'}
                </div>
              </Popup>
            </Marker>
          );
        }

        // User address or friend origin: orange friend marker with photo
        if (origin.markerType === 'userAddress' || origin.markerType === 'friend') {
          const photoUrl = origin.profilePhoto ? getPhotoUrl(origin.profilePhoto) : null;
          const icon = createFriendIcon(
            photoUrl,
            origin.firstName || '',
            origin.lastName || '',
            true, // isRoutePoint - orange styling
            false // isRouteDestination - not destination
          );
          return (
            <Marker
              position={[Number(origin.lat), Number(origin.lng)]}
              icon={icon}
              zIndexOffset={5000}
            >
              <Popup>
                <div>
                  <strong>🚀 Départ</strong>
                  <br />
                  {origin.name || origin.address || `${origin.firstName} ${origin.lastName}`}
                </div>
              </Popup>
            </Marker>
          );
        }

        // Place or unknown: A marker
        return (
          <Marker
            position={[Number(origin.lat), Number(origin.lng)]}
            icon={originIcon}
            zIndexOffset={5000}
          >
            <Popup>
              <div>
                <strong>🚀 Départ</strong>
                <br />
                {origin.name || origin.address || `${Number(origin.lat).toFixed(5)}, ${Number(origin.lng).toFixed(5)}`}
              </div>
            </Popup>
          </Marker>
        );
      })()}

      {/* Waypoint markers - renders based on markerType */}
      {waypoints.map((wp, index) => {
        // Friend waypoint: orange friend marker with photo + step number
        if (wp.markerType === 'friend') {
          const photoUrl = wp.profilePhoto ? getPhotoUrl(wp.profilePhoto) : null;
          const icon = createFriendIcon(
            photoUrl,
            wp.firstName || '',
            wp.lastName || '',
            true, // isRoutePoint - orange styling
            false, // isRouteDestination
            index + 1 // stepNumber - shows badge with step number
          );
          return (
            <Marker
              key={`waypoint-${index}`}
              position={[Number(wp.lat), Number(wp.lng)]}
              icon={icon}
              zIndexOffset={4000 + index}
            >
              <Popup>
                <div>
                  <strong>📍 Étape {index + 1}</strong>
                  <br />
                  {wp.name || `${wp.firstName} ${wp.lastName}`}
                </div>
              </Popup>
            </Marker>
          );
        }

        // Place or unknown: numbered marker
        const waypointIcon = createMarkerIcon(`${index + 1}`, MARKER_COLORS.waypoint);
        return (
          <Marker
            key={`waypoint-${index}`}
            position={[Number(wp.lat), Number(wp.lng)]}
            icon={waypointIcon}
            zIndexOffset={4000 + index}
          >
            <Popup>
              <div>
                <strong>📍 Étape {index + 1}</strong>
                <br />
                {wp.name || wp.address || `${Number(wp.lat).toFixed(5)}, ${Number(wp.lng).toFixed(5)}`}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Destination marker - renders based on markerType */}
      {destination && (() => {
        // Friend destination: orange friend marker with photo + flag
        if (destination.markerType === 'friend') {
          const photoUrl = destination.profilePhoto ? getPhotoUrl(destination.profilePhoto) : null;
          const icon = createFriendIcon(
            photoUrl,
            destination.firstName || '',
            destination.lastName || '',
            true, // isRoutePoint - orange styling
            true  // isRouteDestination - adds flag
          );
          return (
            <Marker
              position={[Number(destination.lat), Number(destination.lng)]}
              icon={icon}
              zIndexOffset={6000}
            >
              <Popup>
                <div>
                  <strong>🏁 Arrivée</strong>
                  <br />
                  {destination.name || `${destination.firstName} ${destination.lastName}`}
                </div>
              </Popup>
            </Marker>
          );
        }

        // Place or unknown: B marker
        return (
          <Marker
            position={[Number(destination.lat), Number(destination.lng)]}
            icon={destinationIcon}
            zIndexOffset={6000}
          >
            <Popup>
              <div>
                <strong>🏁 Arrivée</strong>
                <br />
                {destination.name || destination.address || `${Number(destination.lat).toFixed(5)}, ${Number(destination.lng).toFixed(5)}`}
              </div>
            </Popup>
          </Marker>
        );
      })()}
    </>
  );
};

export default RoutePolyline;
